import React, { useState, useEffect, useCallback } from "react";
import { Loader2, CalendarCheck, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api, formatDate } from "@/lib/api";

const iso = (d) => d.toISOString().slice(0, 10);
const fmtDate = (s) => formatDate(s);
const statusBadge = (r) => {
  if (r.status === "present") return <Badge variant="outline" className="bg-green-100 text-green-800 border-green-300">Present</Badge>;
  if (r.absence_type === "informed") return <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300">Absent (informed)</Badge>;
  return <Badge variant="outline" className="bg-red-100 text-red-700 border-red-300">Absent</Badge>;
};

export default function AttendanceRangeView({ studentId: fixedId, pickerSource }) {
  const [students, setStudents] = useState([]);
  const [sid, setSid] = useState(fixedId || "");
  const [from, setFrom] = useState(iso(new Date(Date.now() - 30 * 864e5)));
  const [to, setTo] = useState(iso(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { setSid(fixedId || ""); }, [fixedId]);
  useEffect(() => {
    if (pickerSource === "teacher") api.get("/teacher/my-students").then(({ data }) => setStudents(data.students || [])).catch(() => {});
  }, [pickerSource]);

  const load = useCallback(async () => {
    if (!sid) { setData(null); return; }
    setLoading(true);
    try { const { data } = await api.get(`/students/${sid}/attendance`, { params: { frm: from, to } }); setData(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not load attendance"); setData(null); }
    finally { setLoading(false); }
  }, [sid, from, to]);
  useEffect(() => { load(); }, [load]);

  const s = data?.summary;
  return (
    <div className="space-y-4" data-testid="attendance-range-view">
      <div className="flex flex-wrap items-end gap-3">
        {pickerSource === "teacher" && (
          <div className="min-w-[220px]">
            <Label className="text-xs">Student</Label>
            <Select value={sid} onValueChange={setSid}>
              <SelectTrigger data-testid="attendance-student-select"><SelectValue placeholder="Choose a student" /></SelectTrigger>
              <SelectContent>
                {students.map((st) => <SelectItem key={st.id} value={st.id}>{st.roll_number} · {st.name} ({st.student_class})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        <div><Label className="text-xs">From</Label><Input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} className="w-[150px]" data-testid="attendance-from" /></div>
        <div><Label className="text-xs">To</Label><Input type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} className="w-[150px]" data-testid="attendance-to" /></div>
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-blue-900" /></div>
      ) : !sid ? (
        <p className="text-sm text-slate-500 py-6 text-center">Select a student to view attendance.</p>
      ) : !data ? null : (
        <>
          {data.student && <div className="text-sm font-semibold text-slate-800">{data.student.name} · {data.student.roll_number} · {data.student.student_class}</div>}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            {[["Classes", s.total, "bg-slate-50 text-slate-700 border-slate-200"],
              ["Present", s.present, "bg-green-50 text-green-700 border-green-200"],
              ["Absent", s.absent, "bg-red-50 text-red-700 border-red-200"],
              ["Informed", s.informed, "bg-amber-50 text-amber-700 border-amber-200"],
              ["Attendance", s.pct == null ? "—" : s.pct + "%", "bg-blue-50 text-blue-800 border-blue-200"]].map(([lbl, val, cls]) => (
              <div key={lbl} className={`rounded-xl border p-3 text-center ${cls}`} data-testid={`attendance-stat-${lbl.toLowerCase()}`}>
                <div className="text-xl font-bold">{val}</div><div className="text-[11px] opacity-80">{lbl}</div>
              </div>
            ))}
          </div>
          <div className="rounded-xl border border-slate-200 overflow-hidden" data-testid="attendance-records">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs"><tr>
                <th className="text-left px-3 py-2">Date</th><th className="text-left px-3 py-2">Class</th>
                <th className="text-left px-3 py-2">Status</th><th className="text-left px-3 py-2">Marked by</th>
              </tr></thead>
              <tbody>
                {data.records.map((r, i) => (
                  <tr key={i} className="border-t border-slate-100"><td className="px-3 py-2">{fmtDate(r.date)}</td>
                    <td className="px-3 py-2 text-slate-600">{r.klass}</td>
                    <td className="px-3 py-2">{statusBadge(r)}</td>
                    <td className="px-3 py-2 text-xs text-slate-500">{r.teacher || "—"}</td></tr>
                ))}
              </tbody>
            </table>
            {data.records.length === 0 && <p className="text-center text-slate-400 py-8 flex items-center justify-center gap-2"><CalendarCheck className="h-4 w-4" /> No attendance recorded in this range.</p>}
          </div>
        </>
      )}
    </div>
  );
}
